(() => {
  'use strict';
  if (window.InfinityCampaign) return;
  const node = document.getElementById('infinity-bf-config');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch (_) { return; }
  const text = config.text || {};
  const bogoIds = new Set((config.bogoIds || []).map(String));
  const gifts = config.gifts || [];
  const giftIds = new Set(gifts.map(g => String(g.variantId)));
  const arabic = document.documentElement.lang.toLowerCase().startsWith('ar');
  const formatter = new Intl.NumberFormat(arabic ? 'ar-EG' : 'en-EG', { style: 'currency', currency: 'EGP', maximumFractionDigits: 2 });
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let cart = config.cart || { items: [], total_price: 0, item_count: 0, currency: 'EGP' };
  let giftTask = null, giftTimer, giftBusy = false, failureSignature = '', lastState = '';
  let selectedVariant = String(config.selectedVariant || '');
  const patchedPrices = new WeakSet();
  const format = value => formatter.format(Math.max(0, Number(value) || 0) / 100);
  const fillText = (template, values = {}) => Object.entries(values).reduce((result, [key, value]) => String(result).replaceAll(`__${key.toUpperCase()}__`, String(value)), template || '');
  const setText = (element, value) => { if (element && element.textContent !== value) element.textContent = value; };
  const isGift = item => giftIds.has(String(item.variant_id || item.id));

  function campaignState(now = Date.now()) {
    if (!config.enabled) return 'disabled';
    const start = Date.parse(config.start), end = Date.parse(config.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 'disabled';
    if (now < start) return 'upcoming';
    return now < end ? 'active' : 'expired';
  }
  function computeRewards(value, now = Date.now()) {
    const items = value?.items || [];
    const state = campaignState(now);
    const currency = value?.currency || 'EGP';
    const giftTotal = items.filter(isGift).reduce((sum, item) => sum + (Number(item.final_line_price) || 0), 0);
    const subtotal = Math.max(0, (Number(value?.total_price) || 0) - giftTotal);
    const active = config.enabled && now >= Date.parse(config.rewardsStart || config.start) && now < Date.parse(config.end) && currency === 'EGP';
    const shipping = active && subtotal >= config.shipping;
    const giftType = active && subtotal >= config.tote ? 'tote' : active && subtotal >= config.pouch ? 'pouch' : null;
    const next = subtotal < config.shipping ? 'shipping' : subtotal < config.pouch ? 'pouch' : subtotal < config.tote ? 'tote' : null;
    const bogoCount = items.filter(item => bogoIds.has(String(item.product_id)) && !isGift(item)).reduce((sum, item) => sum + Math.max(0, Number(item.quantity) || 0), 0);
    const bogoApplied = state === 'active' && items.some(item => (item.line_level_discount_allocations || item.discount_allocations || []).some(allocation => Number(allocation.amount) > 0 && (allocation.discount_application?.title || allocation.title) === config.bogoDiscountTitle));
    return {
      state, active, subtotal, shipping, giftType, next,
      remaining: next ? Math.max(0, config[next] - subtotal) : 0,
      progress: Math.max(0, Math.min(100, subtotal / config.tote * 100)),
      bogoCount, bogoRemaining: bogoCount === 0 ? 2 : bogoCount % 2, bogoStep: bogoCount === 0 ? 0 : bogoCount % 2 || 2, bogoApplied
    };
  }
  function desiredGift(value, now = Date.now()) {
    const kind = computeRewards(value, now).giftType;
    return kind ? gifts.find(gift => gift.type === kind) || null : null;
  }
  function giftPlan(value, now = Date.now()) {
    const desired = desiredGift(value, now);
    const lines = (value?.items || []).filter(isGift);
    const updates = {};
    let kept = false;
    for (const item of lines) {
      const wanted = desired && String(item.variant_id || item.id) === String(desired.variantId);
      if (wanted && !kept) {
        if (Number(item.quantity) !== 1) updates[item.key || String(item.variant_id || item.id)] = 1;
        kept = true;
      } else updates[item.key || String(item.variant_id || item.id)] = 0;
    }
    return { desired, updates, add: desired && !kept ? desired : null };
  }
  function validateGifts(value) {
    const plan = giftPlan(value);
    return Object.keys(plan.updates).length || plan.add ? [text.giftRetry || 'Your gift is being updated. Please try again.'] : [];
  }
  function giftRequestError(kind, data, value) {
    const desired = desiredGift(value);
    const quantities = new Map((value?.items || []).filter(isGift).map(item => [String(item.variant_id || item.id), Number(item.quantity) || 0]));
    let touchesGift = false;
    if (kind === 'add') {
      for (const item of data.items || (data.id ? [data] : [])) {
        const id = String(item.id || '');
        if (giftIds.has(id)) { touchesGift = true; quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity ?? 1)); }
      }
    } else if (kind === 'change') {
      const item = data.line ? value.items?.[Number(data.line) - 1] : value.items?.find(item => item.key === String(data.id) || String(item.variant_id || item.id) === String(data.id));
      if (item && isGift(item)) { touchesGift = true; quantities.set(String(item.variant_id || item.id), Math.max(0, Number(data.quantity) || 0)); }
    } else if (kind === 'update' && data.updates) {
      for (const [key, quantity] of Object.entries(data.updates)) {
        const item = Array.isArray(data.updates) ? value.items?.[Number(key)] : value.items?.find(item => item.key === key || String(item.variant_id || item.id) === key);
        const id = item ? String(item.variant_id || item.id) : key;
        if (giftIds.has(id)) { touchesGift = true; quantities.set(id, Math.max(0, Number(quantity) || 0)); }
      }
    }
    if (!touchesGift) return null;
    const invalid = [...quantities].some(([id, quantity]) => quantity > 0 && (!desired || id !== String(desired.variantId) || quantity !== 1));
    return invalid ? text.giftRestricted || 'Gifts are added automatically when your cart qualifies.' : null;
  }
  function currentCart() { return window.InfinityCart?.getCart?.() || cart; }
  function signature(value) { return `${campaignState()}:${value.currency}:${value.total_price}:` + (value.items || []).map(item => `${item.key || item.id}:${item.quantity}`).join('|'); }
  function progressBar(element, value, max, completed) {
    if (!element) return;
    element.setAttribute('aria-valuenow', String(Math.max(0, Math.min(max, value))));
    element.setAttribute('aria-valuemax', String(max));
    const fill = element.querySelector('span');
    if (fill) fill.style.width = `${Math.max(0, Math.min(100, value / max * 100))}%`;
    element.classList.toggle('is-complete', completed);
  }
  function renderRewards() {
    const state = computeRewards(cart);
    document.querySelectorAll('[data-inf-rewards]').forEach(element => {
      const message = !state.active ? text.giftPreview : !state.next ? text.allRewards : fillText(text.nextReward, { amount: format(state.remaining), reward: text[state.next] });
      setText(element.querySelector('[data-inf-reward-message]'), message);
      progressBar(element.querySelector('[data-inf-reward-bar]'), state.active ? state.subtotal : 0, config.tote, state.active && !state.next);
      for (const name of ['shipping', 'pouch', 'tote']) {
        const tier = element.querySelector(`[data-inf-tier="${name}"]`);
        if (tier) tier.dataset.state = name === 'pouch' && state.giftType === 'tote' ? 'upgraded' : state.active && state.subtotal >= config[name] ? 'unlocked' : 'locked';
      }
      const gift = element.querySelector('[data-inf-gift]');
      if (gift) {
        gift.hidden = !state.giftType;
        setText(gift, state.giftType ? fillText(text.giftEligible, { gift: state.giftType === 'pouch' ? 'Pouch' : 'Tote Bag' }) : '');
      }
      setText(element.querySelector('[data-inf-bogo-count]'), fillText(text.bogoCount, { count: state.bogoCount }));
      const bogoMessage = state.state === 'upcoming' ? text.bogoUpcoming : state.bogoRemaining ? fillText(text.bogoRemaining, { count: state.bogoRemaining }) : state.bogoApplied ? text.bogoApplied : text.bogoReady;
      setText(element.querySelector('[data-inf-bogo-message]'), bogoMessage);
      progressBar(element.querySelector('[data-inf-bogo-bar]'), state.bogoStep, 2, state.state === 'active' && state.bogoRemaining === 0 && state.bogoCount > 0);
    });
  }
  function renderClock() {
    const state = campaignState();
    document.documentElement.dataset.infBfState = state;
    const visible = state === 'active' || state === 'upcoming';
    document.querySelectorAll('[data-inf-campaign]').forEach(element => { element.hidden = !visible; });
    if (state !== lastState) {
      lastState = state;
      renderRewards();
      scheduleGiftSync();
    }
    if (!visible) return;
    const end = Date.parse(state === 'upcoming' ? config.start : config.end);
    const seconds = Math.max(0, Math.floor((end - Date.now()) / 1000));
    const values = { days: Math.floor(seconds / 86400), hours: Math.floor(seconds / 3600) % 24, minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60 };
    document.querySelectorAll('[data-inf-countdown]').forEach(element => {
      setText(element.querySelector('[data-inf-clock-label]'), state === 'upcoming' ? text.starts : text.ends);
      Object.entries(values).forEach(([unit, value]) => setText(element.querySelector(`[data-inf-${unit}]`), String(value).padStart(2, '0')));
    });
  }
  function renderVariant(event) {
    const detail = event?.detail;
    const selected = detail?.variant || detail?.resource || detail?.data?.variant;
    if (selected?.id) selectedVariant = String(selected.id);
    else {
      const main = document.querySelector('hdt-main-product, [data-main-product], .hdt-product-info');
      const field = main?.querySelector('form [name="id"]');
      if (field?.value) selectedVariant = String(field.value);
    }
    const variant = (config.variants || []).find(value => String(value.id) === selectedVariant) || config.variants?.[0];
    if (!variant) return;
    const inventoryText = !variant.available ? text.stockUnavailable : variant.tracked && !variant.continueSelling && variant.inventory > 0 && variant.inventory <= 10 ? fillText(text.stockLow, { count: variant.inventory }) : text.stockAvailable;
    document.querySelectorAll(`[data-inf-stock][data-product-id="${config.productId}"]`).forEach(element => { setText(element, inventoryText); element.dataset.unavailable = String(!variant.available); });
  }
  function hideGiftControls() {
    const handles = gifts.map(gift => gift.handle).filter(Boolean);
    document.querySelectorAll('hdt-card-product').forEach(card => {
      if (handles.some(handle => card.querySelector(`a[href*="/products/${handle}"]`))) card.hidden = true;
    });
    document.querySelectorAll('[data-inf-gift-line]').forEach(line => line.dataset.infGiftProtected = 'true');
  }
  function sectionsData() {
    const ids = [...document.querySelectorAll('hdt-cart-drawer[section-id], hdt-main-cart[section-id]')].map(element => element.getAttribute('section-id')).filter(Boolean);
    return { sections: [...new Set(ids)].slice(0, 5), sections_url: location.pathname };
  }
  async function mutateCart(path, payload) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await window.fetch(`${root}cart/${path}.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...payload, ...sectionsData() }), signal: controller.signal });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Gift unavailable');
      cart = currentCart();
      const themedCart = { ...cart, sections: result.sections || {} };
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: result, cartData: themedCart, sourceId: 'infinity-black-friday', source: 'infinity-bf-gifts', actionAfterATC: 'no_action', data: { sections: result.sections || {}, productId: config.productId } } }));
      return result;
    } finally { clearTimeout(timer); }
  }
  function giftStatus(message) {
    document.querySelectorAll('[data-inf-gift-status]').forEach(element => { setText(element, message || ''); element.hidden = !message; });
  }
  function syncGifts(force = false) {
    if (!config.autoGifts || !gifts.length) return Promise.resolve();
    if (giftTask) return giftTask;
    if (!force && failureSignature === signature(currentCart())) return Promise.resolve();
    giftBusy = true;
    giftTask = (async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        cart = currentCart();
        const plan = giftPlan(cart);
        if (Object.keys(plan.updates).length) { await mutateCart('update', { updates: plan.updates }); continue; }
        if (plan.add) {
          if (plan.add.available === false) throw new Error('Gift unavailable');
          await mutateCart('add', { items: [{ id: Number(plan.add.variantId), quantity: 1, properties: { _infinity_bf_gift: plan.add.type, _infinity_bf_campaign: config.campaignKey } }] });
          continue;
        }
        giftStatus('');
        failureSignature = '';
        return;
      }
      if (validateGifts(currentCart()).length) throw new Error('Gift update incomplete');
    })().catch(error => {
      failureSignature = signature(currentCart());
      giftStatus(text.giftUnavailable || 'Your gift could not be added. Please try again.');
      if (force) throw error;
    }).finally(() => {
      giftBusy = false;
      giftTask = null;
      cart = currentCart();
      renderRewards();
      hideGiftControls();
    });
    return giftTask;
  }
  function scheduleGiftSync() {
    clearTimeout(giftTimer);
    giftTimer = setTimeout(() => syncGifts(), 180);
  }
  function accept(value) {
    if (!value || !Array.isArray(value.items) || !Number.isFinite(value.total_price)) return;
    cart = value;
    renderRewards();
    hideGiftControls();
    if (!giftBusy) scheduleGiftSync();
  }
  function init() {
    accept(currentCart());
    renderClock();
    renderVariant();
    document.querySelectorAll('.hdt-product__price').forEach(element => {
      if (patchedPrices.has(element)) return;
      patchedPrices.add(element);
      new MutationObserver(() => requestAnimationFrame(() => renderVariant())).observe(element, { childList: true, subtree: true, characterData: true });
    });
  }
  window.InfinityCampaign = { version: '1.1.0-bogo', campaignState, computeRewards, desiredGift, giftPlan, validateGifts, giftRequestError, syncGifts, get busy() { return giftBusy; }, giftIds };
  window.addEventListener('infinity:cart-updated', event => accept(event.detail?.cart));
  document.addEventListener('variant:update', renderVariant);
  document.addEventListener('variant:selected', event => requestAnimationFrame(() => renderVariant(event)));
  for (const name of ['cart:drawer:render', 'cart:main:render', 'shopify:section:load', 'filter:update']) document.addEventListener(name, init);
  document.addEventListener('dialog:opening', init);
  window.addEventListener('pageshow', init);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) init(); });
  setInterval(() => { if (!document.hidden) renderClock(); }, 1000);
  init();
})();
